import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { BsGrid3X3GapFill, BsFillEnvelopeFill } from "react-icons/bs";
import BellIconWithNotifications, {
  NotificationWidget,
} from "../notifications/notificationwidget";
import { IoMap } from "react-icons/io5";
import { FiRefreshCw } from "react-icons/fi";
import "bootstrap/dist/css/bootstrap.min.css";

const StateRenewal = () => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editingState, setEditingState] = useState("");
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Function to fetch all state non-renewal days
  const fetchStateData = async () => {
    try {
      setLoading(true);
      setError("");
      
      const token = localStorage.getItem("accessToken");
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await fetch(`${apiUrl}state-non-renewal-days`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Transform API data to match component structure
        const transformedData = data.data.map(item => ({
          state: item.State,
          days: item.NonRenewalDays,
          isEditing: false
        }));
        setStates(transformedData);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to fetch state data');
      }
      
    } catch (error) {
      console.error('Error fetching state data:', error);
      setError('Failed to fetch state non-renewal days');
    } finally {
      setLoading(false);
    }
  };

  // Function to update state non-renewal days
  const updateStateData = async (state, nonRenewalDays) => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("accessToken");
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${apiUrl}update-state-non-renewal-days`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          state: state,
          nonRenewalDays: parseInt(nonRenewalDays)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Update local state
      setStates(prevStates => 
        prevStates.map(stateItem => 
          stateItem.state === state 
            ? { ...stateItem, days: parseInt(nonRenewalDays), isEditing: false }
            : stateItem
        )
      );

      setSuccess(`Successfully updated ${state} to ${nonRenewalDays} days`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess("");
      }, 3000);

    } catch (error) {
      console.error('Error updating state data:', error);
      setError(error.message || 'Failed to update state non-renewal days');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setError("");
      }, 5000);
    } finally {
      setSaving(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchStateData();
  }, []);

  const handleEdit = (state, currentDays) => {
    setEditingState(state);
    setEditValue(currentDays.toString());
    setError(""); // Clear any existing errors
    setSuccess(""); // Clear any existing success messages
  };

  const handleSave = async () => {
    if (!editValue || editValue <= 0) {
      setError("Please enter a valid number of days (greater than 0).");
      return;
    }

    await updateStateData(editingState, editValue);
    setEditingState("");
    setEditValue("");
  };

  const handleCancel = () => {
    setEditingState("");
    setEditValue("");
    setError("");
    setSuccess("");
  };

  const handleInputChange = (e) => {
    setEditValue(e.target.value);
    setError(""); // Clear any existing errors
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleRefresh = () => {
    fetchStateData();
  };

  return (
    <>
      <div className="dashboard-title mb-3">
        <h1>State Renewal</h1>
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh data"
          >
            <FiRefreshCw className={loading ? "spin" : ""} />
          </button>
          <BellIconWithNotifications />
          <div className="dropdown-container">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                className="rounded-circle border-0 bg-transparent"
              >
                <div
                  className="menu-icon"
                  style={{
                    fontSize: "1.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsGrid3X3GapFill />
                </div>
              </Dropdown.Toggle>

              <Dropdown.Menu className="dropdown-menu-custom">
                <Dropdown.Item
                  href="/dashboard"
                  className="dropdown-item-custom"
                >
                  <BsFillEnvelopeFill className="dropdown-icon" /> Quote/Bind
                  Email
                </Dropdown.Item>
                <Dropdown.Item
                  href="/geographical-concentration"
                  className="dropdown-item-custom"
                >
                  <IoMap className="dropdown-icon" /> Geographical Concentration
                </Dropdown.Item>
                <Dropdown.Item
                  href="/renewals-dashboard"
                  className="dropdown-item-custom"
                >
                  <FiRefreshCw className="dropdown-icon" /> Renewals
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>

      <div className="content-body">
        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-3" role="alert">
            {error}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setError("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
            {success}
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setSuccess("")}
              aria-label="Close"
            ></button>
          </div>
        )}

        {/* Loading indicator */}
        {loading && (
          <div className="text-center mb-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2">Loading state data...</div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ backgroundColor: "#f8f9fa" }}>
              <tr>
                <th className="px-4 py-3 fw-semibold">State</th>
                <th className="px-4 py-3 fw-semibold">Non-Renewal Days (Prior to Expiration)</th>
                <th className="px-4 py-3 fw-semibold text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {states.length === 0 && !loading ? (
                <tr>
                  <td colSpan="3" className="text-center py-4 text-muted">
                    No state data available
                  </td>
                </tr>
              ) : (
                states.map((stateItem) => (
                  <tr key={stateItem.state}>
                    <td className="px-4 py-3 fw-medium">{stateItem.state}</td>
                    <td className="px-4 py-3">
                      {editingState === stateItem.state ? (
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="number"
                            className="form-control"
                            value={editValue}
                            onChange={handleInputChange}
                            onKeyPress={handleKeyPress}
                            style={{ width: "80px" }}
                            disabled={saving}
                            min="1"
                            max="365"
                            autoFocus
                          />
                          <button
                            className="btn btn-success btn-sm rounded-circle d-flex align-items-center justify-content-center"
                            onClick={handleSave}
                            disabled={saving || !editValue}
                            style={{ width: "28px", height: "28px" }}
                            title="Save changes"
                          >
                            {saving ? (
                              <span 
                                className="spinner-border spinner-border-sm" 
                                role="status" 
                                aria-hidden="true"
                                style={{ width: "12px", height: "12px" }}
                              ></span>
                            ) : (
                              "✓"
                            )}
                          </button>
                          <button
                            className="btn btn-danger btn-sm rounded-circle d-flex align-items-center justify-content-center"
                            onClick={handleCancel}
                            disabled={saving}
                            style={{ width: "28px", height: "28px" }}
                            title="Cancel editing"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <span>{stateItem.days}</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="d-flex justify-content-end">
                        {editingState !== stateItem.state && (
                          <button
                            className="btn btn-sm text-white"
                            onClick={() => handleEdit(stateItem.state, stateItem.days)}
                            disabled={editingState !== "" || saving}
                            style={{ backgroundColor: "#122066", border: "none" }}
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style jsx>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default StateRenewal;